'use client';

import { useState } from 'react';
import { useMediaQuery } from '@/app/hooks/use-media-query';

const FOOD_ICONS: Record<string, string> = {
  'Bánh mì': '🍞',
  'Cơm': '🍚',
  'Fastfood': '🍔',
  'Lẩu': '🍲',
  'Món cuốn': '🥢',
  'Món khô': '🍜',
  'Món nước': '🥣',
  'Nhậu nhẹt': '🍻',
  'Quán nước': '🧋',
  'Sang chảnh - Không gian xinh': '✨',
  'Tráng miệng': '🍰',
};

type FilterChipsProps = {
  foodTypes: string[];
  selected: string;
  onSelect: (type: string) => void;
};

function ChipButton({ type, selected, onSelect, label }: { type: string; selected: string; onSelect: (t: string) => void; label: string }) {
  return (
    <button
      onClick={() => onSelect(type)}
      className={`flex-shrink-0 px-4 h-10 rounded-full text-sm font-medium transition-all duration-200 active:scale-95 whitespace-nowrap focus-visible:ring-2 focus-visible:ring-coral-400 focus-visible:ring-offset-2 ${
        selected === type
          ? 'bg-coral-500 text-white shadow-md ring-2 ring-coral-200 scale-105'
          : 'border border-coral-200 bg-white text-gray-700 shadow-sm hover:bg-coral-50 hover:border-coral-300 hover:shadow'
      }`}
    >
      {label}
    </button>
  );
}

/* Mobile: filter button that opens a modal with all chips */
function MobileFilterModal({ foodTypes, selected, onSelect }: FilterChipsProps) {
  const [open, setOpen] = useState(false);
  const selectedLabel = selected
    ? (selected.startsWith('❤️') ? selected : `${FOOD_ICONS[selected] || '🍽️'} ${selected}`)
    : '🍽️ Tất cả';

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 h-10 px-4 rounded-full border border-coral-200 bg-white text-sm font-medium text-gray-700 shadow-sm active:scale-95"
      >
        <span>{selectedLabel}</span>
        <span className="text-xs text-gray-600">▼</span>
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/30 z-50" onClick={() => setOpen(false)} />
          {/* Modal */}
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-cream rounded-t-2xl shadow-2xl p-4 pb-8 max-h-[60vh] overflow-y-auto">
            <div className="mx-auto w-12 h-1.5 rounded-full bg-coral-200 mb-4" />
            <p className="text-sm font-semibold text-gray-700 mb-3">Chọn loại món</p>
            <div className="flex flex-wrap gap-2">
              <ChipButton type="" selected={selected} onSelect={(t) => { onSelect(t); setOpen(false); }} label="🍽️ Tất cả" />
              {foodTypes.map((type) => {
                const label = type.startsWith('❤️') ? type : `${FOOD_ICONS[type] || '🍽️'} ${type}`;
                return (
                  <ChipButton key={type} type={type} selected={selected} onSelect={(t) => { onSelect(t); setOpen(false); }} label={label} />
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}

/* Desktop: horizontal scrollable chips */
function DesktopChips({ foodTypes, selected, onSelect }: FilterChipsProps) {
  return (
    <div className="flex flex-wrap gap-2">
      <ChipButton type="" selected={selected} onSelect={onSelect} label="🍽️ Tất cả" />
      {foodTypes.map((type) => {
        const label = type.startsWith('❤️') ? type : `${FOOD_ICONS[type] || '🍽️'} ${type}`;
        return <ChipButton key={type} type={type} selected={selected} onSelect={onSelect} label={label} />;
      })}
    </div>
  );
}

export function FilterChips({ foodTypes, selected, onSelect }: FilterChipsProps) {
  const isTabletUp = useMediaQuery('(min-width: 768px)');

  if (isTabletUp) {
    return <DesktopChips foodTypes={foodTypes} selected={selected} onSelect={onSelect} />;
  }
  return <MobileFilterModal foodTypes={foodTypes} selected={selected} onSelect={onSelect} />;
}
